import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Table,
    Badge
} from "reactstrap";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { getJokerPnL, getGeneralPnL, getJokerSessionById, getAllUsers, getOperatorDropdown, exportTransactions, getOperatorUsers } from "../../../../helpers/apis";
import CommonDataTable from "../../../../common/DataTable";
import { GeneralPnLColumns, JokerPnLColumns } from "../../../../Components/Common/columns/PnLCols";
import { getAmountColorClass } from "../../../../Components/Common/columns/BidTranjections";
import TransactionTab from "./TransactionTab";

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const yesterdayStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const last7DaysStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const thisMonthStartStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const PnL = () => {
    // Default to "2" (General P&L) as first tab or rename logic. 
    // User requested: "tab 1st tab need a p&l and 2nd tab need a joker".
    // So Tab "1" = General P&L, Tab "2" = Joker.
    const [activeTab, setActiveTab] = useState("1");

    // Loading States
    const [generalLoading, setGeneralLoading] = useState(false);
    const [jokerLoading, setJokerLoading] = useState(false);

    // Filters - Separate for each tab
    const [generalStartDate, setGeneralStartDate] = useState("");
    const [generalEndDate, setGeneralEndDate] = useState("");
    const [generalDateRange, setGeneralDateRange] = useState([]);
    const [jokerStartDate, setJokerStartDate] = useState("");
    const [jokerEndDate, setJokerEndDate] = useState("");
    const [jokerDateRange, setJokerDateRange] = useState([]);

    // Get current filter values based on active tab
    const startDate = activeTab === "1" ? generalStartDate : jokerStartDate;
    const endDate = activeTab === "1" ? generalEndDate : jokerEndDate;
    const dateRange = activeTab === "1" ? generalDateRange : jokerDateRange;

    // General P&L Data & Params
    const [generalData, setGeneralData] = useState({ summary: {}, history: [], rounds: [], pagination: { total: 0 } });
    const [generalParams, setGeneralParams] = useState({ page: 1, limit: 10 });
    const generalParamsRef = useRef({ page: 1, limit: 10 });
    const [generalSummary, setGeneralSummary] = useState({ totalBets: "0", totalPayout: "0", adminPnL: "0", totalRounds: "0" });

    // Sync ref when params change from external sources
    useEffect(() => {
        generalParamsRef.current = generalParams;
    }, [generalParams]);

    // Joker Data & Params
    const [jokerData, setJokerData] = useState({ summary: {}, history: [], pagination: { total: 0 } });
    const [jokerParams, setJokerParams] = useState({ page: 1, limit: 10 });
    const jokerParamsRef = useRef({ page: 1, limit: 10 });

    // Sync ref when params change from external sources
    useEffect(() => {
        jokerParamsRef.current = jokerParams;
    }, [jokerParams]);

    // Transaction Modal State
    const [transactionModal, setTransactionModal] = useState(false);
    const [transactionData, setTransactionData] = useState(null);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [userDetailsMap, setUserDetailsMap] = useState({});

    // Operator & User Filter State - Separate for each tab
    const [generalOperatorId, setGeneralOperatorId] = useState("");
    const [generalUserId, setGeneralUserId] = useState("");
    const [jokerOperatorId, setJokerOperatorId] = useState("");
    const [jokerUserId, setJokerUserId] = useState("");
    const [generalUsers, setGeneralUsers] = useState([]);
    const [jokerUsers, setJokerUsers] = useState([]);
    const [operators, setOperators] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Export Loading State
    const [isExporting, setIsExporting] = useState(false);

    // Refresh Loading State
    const [refreshLoading, setRefreshLoading] = useState(false);

    // Winners Modal State
    const [winnersModal, setWinnersModal] = useState(false);
    const [winnersData, setWinnersData] = useState([]);
    const [roundNumber, setRoundNumber] = useState(null);
    const [roundDetails, setRoundDetails] = useState(null);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            // Don't clear filters - let each tab maintain its own filter state
        }
    };

    // Game Mode state
    const [generalGameMode, setGeneralGameMode] = useState("all");

    // Fetch General P&L
    const fetchGeneralData = useCallback(async (params = {}) => {
        setGeneralLoading(true);
        try {
            // Update ref and state
            const currentPage = params.page || generalParamsRef.current.page;
            const currentLimit = params.limit || generalParamsRef.current.limit;

            if (params.page || params.limit) {
                const newParams = { ...generalParamsRef.current, ...params };
                generalParamsRef.current = newParams;
                setGeneralParams(newParams);
            }

            const query = {
                startDate: generalStartDate,
                endDate: generalEndDate,
                page: currentPage,
                limit: currentLimit,
                gameType: 'lt',
                isDownload: params.isDownload,
                operatorId: generalOperatorId || undefined,
                userAcNumber: generalUserId || undefined,
                gameMode: generalGameMode !== 'all' ? generalGameMode : undefined
            };

            const res = await getGeneralPnL(query);
            if (res && res.data) {
                setGeneralData(res.data);

                // 
                // The API returns summary with totalBetsAmount, totalPayoutAmount, adminProfit, totalRounds
                const summary = res.data.summary || {};

                // Format numbers with commas
                const formatNumber = (num) => {
                    if (num === undefined || num === null) return "0";
                    return new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                    }).format(num);
                };

                // Format profit margin if exists
                const formatProfitMargin = (margin) => {
                    if (margin === undefined || margin === null) return "0%";
                    return `${margin.toFixed(2)}%`;
                };

                setGeneralSummary({
                    totalBets: formatNumber(summary.totalBetsAmount),
                    totalPayout: formatNumber(summary.totalPayoutAmount),
                    adminPnL: formatNumber(summary.adminProfit),
                    totalRounds: formatNumber(summary.totalRounds),
                    profitMargin: formatProfitMargin(summary.profitMargin),
                    gameName: summary.gameName || "Lucky Target"
                });
            } else {
                toast.error("Failed to fetch General P&L data");
            }
        } catch (err) {
            console.error("Error fetching General P&L", err);
            toast.error(err.response?.data?.message || err.message || "Error fetching General P&L");
        } finally {
            setGeneralLoading(false);
        }
    }, [generalStartDate, generalEndDate, generalOperatorId, generalUserId, generalGameMode]);

    // Fetch Joker P&L
    const fetchJokerData = useCallback(async (params = {}) => {
        setJokerLoading(true);
        try {
            // Update ref and state
            const currentPage = params.page || jokerParamsRef.current.page;
            const currentLimit = params.limit || jokerParamsRef.current.limit;

            if (params.page || params.limit) {
                const newParams = { ...jokerParamsRef.current, ...params };
                jokerParamsRef.current = newParams;
                setJokerParams(newParams);
            }

            const query = {
                startDate: jokerStartDate,
                endDate: jokerEndDate,
                page: currentPage,
                limit: currentLimit,
                gameType: 'lt',
                isDownload: params.isDownload,
                operatorId: jokerOperatorId || undefined,
                userAcNumber: jokerUserId || undefined
            };

            const res = await getJokerPnL(query);
            if (res && res.data) {
                setJokerData(res.data);
            } else {
                toast.error("Failed to fetch Joker P&L data");
            }
        } catch (err) {
            console.error("Error fetching Joker P&L", err);
            toast.error(err.response?.data?.message || err.message || "Error fetching Joker P&L");
        } finally {
            setJokerLoading(false);
        }
    }, [jokerStartDate, jokerEndDate, jokerOperatorId, jokerUserId]);

    // Ensure filters are empty on component mount
    useEffect(() => {
        setGeneralStartDate("");
        setGeneralEndDate("");
        setGeneralDateRange([]);
        setJokerStartDate("");
        setJokerEndDate("");
        setJokerDateRange([]);
        setGeneralOperatorId("");
        setGeneralUserId("");
        setJokerOperatorId("");
        setJokerUserId("");
    }, []);

    useEffect(() => {
        if (activeTab === "1") {
            fetchGeneralData({ page: 1 });
        } else {
            fetchJokerData({ page: 1 });
        }
    }, [activeTab, generalOperatorId, generalUserId, jokerOperatorId, jokerUserId, generalStartDate, generalEndDate, jokerStartDate, jokerEndDate]);

    // Fetch Operators for dropdown
    useEffect(() => {
        const fetchOperators = async () => {
            try {
                const res = await getOperatorDropdown();
                if (res && res.data) {
                    setOperators(res.data);
                }
            } catch (err) {
                console.error("Error fetching operators", err);
            }
        };
        fetchOperators();
    }, []);

    // Fetch users for general tab
    useEffect(() => {
        const fetchGenUsers = async () => {
            if (!generalOperatorId) {
                setGeneralUsers([]);
                setGeneralUserId("");
                return;
            }
            setLoadingUsers(true);
            try {
                const res = await getOperatorUsers(generalOperatorId);
                if (res && res.data) {
                    const userList = res.data.users || (Array.isArray(res.data) ? res.data : []);
                    setGeneralUsers(userList);
                } else {
                    setGeneralUsers([]);
                }
            } catch (err) {
                console.error("Error fetching general users", err);
                setGeneralUsers([]);
            } finally {
                setLoadingUsers(false);
            }
        };
        if (generalOperatorId) fetchGenUsers();
    }, [generalOperatorId]);

    // Fetch users for joker tab
    useEffect(() => {
        const fetchJokUsers = async () => {
            if (!jokerOperatorId) {
                setJokerUsers([]);
                setJokerUserId("");
                return;
            }
            setLoadingUsers(true);
            try {
                const res = await getOperatorUsers(jokerOperatorId);
                if (res && res.data) {
                    const userList = res.data.users || (Array.isArray(res.data) ? res.data : []);
                    setJokerUsers(userList);
                } else {
                    setJokerUsers([]);
                }
            } catch (err) {
                console.error("Error fetching joker users", err);
                setJokerUsers([]);
            } finally {
                setLoadingUsers(false);
            }
        };
        if (jokerOperatorId) fetchJokUsers();
    }, [jokerOperatorId]);

    const handleFilter = () => {
        if (activeTab === "1") {
            fetchGeneralData({ page: 1 });
        } else {
            fetchJokerData({ page: 1 });
        }
    };

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshLoading(true);
        try {
            if (activeTab === "1") {
                await fetchGeneralData({ page: generalParams.page });
            } else {
                await fetchJokerData({ page: jokerParams.page });
            }
        } finally {
            setRefreshLoading(false);
        }
    };

    // Fetch user details for transactions
    const fetchUserDetails = async (userIds) => {
        if (!userIds || userIds.length === 0) return {};

        try {
            // Fetch all users and create a map
            const res = await getAllUsers({ limit: 1000 }); // Get all users to map IDs
            if (res && res.status === 1) {
                const users = res.data?.users || res.users || [];
                const userMap = {};
                users.forEach(user => {
                    if (user._id) {
                        userMap[user._id] = {
                            name: user.name || user.email?.split('@')[0] || 'User',
                            email: user.email || 'N/A'
                        };
                    }
                });
                return userMap;
            }
        } catch (err) {
            console.error("Error fetching user details", err);
        }
        return {};
    };

    // Handle Transaction Icon Click
    const handleTransactionClick = async (jokerId) => {
        if (!jokerId) {
            toast.error("Joker ID not found");
            return;
        }

        setLoadingTransactions(true);
        setTransactionModal(true);
        try {
            const res = await getJokerSessionById(jokerId);
            if (res && res.statusCode === 200 && res.status === 1) {
                // Handle response structure: res.data contains the session data
                const sessionData = res.data || res;
                if (sessionData) {
                    // Extract all transactions from jokerRoundDetails and flatten them
                    let allTransactions = [];
                    if (sessionData.jokerRoundDetails && Array.isArray(sessionData.jokerRoundDetails)) {
                        sessionData.jokerRoundDetails.forEach(round => {
                            if (round.transactions && Array.isArray(round.transactions) && round.transactions.length > 0) {
                                allTransactions = allTransactions.concat(round.transactions);
                            }
                        });
                    }

                    // Also check for top-level transactions (for backward compatibility)
                    if (sessionData.transactions && Array.isArray(sessionData.transactions)) {
                        allTransactions = allTransactions.concat(sessionData.transactions);
                    }

                    // Add flattened transactions to sessionData
                    const sessionDataWithTransactions = {
                        ...sessionData,
                        transactions: allTransactions
                    };

                    setTransactionData(sessionDataWithTransactions);

                    // Fetch user details for transactions
                    if (allTransactions.length > 0) {
                        const userIds = [...new Set(allTransactions
                            .map(t => t.userId)
                            .filter(id => id))];
                        if (userIds.length > 0) {
                            const userMap = await fetchUserDetails(userIds);
                            setUserDetailsMap(userMap);
                        }
                    }
                } else {
                    toast.error("Session data not found");
                    setTransactionData(null);
                }
            } else {
                toast.error(res?.message || "Failed to fetch transaction details");
                setTransactionData(null);
            }
        } catch (err) {
            console.error("Error fetching transaction details", err);
            toast.error(err.response?.data?.message || err.message || "Error fetching transaction details");
            setTransactionData(null);
        } finally {
            setLoadingTransactions(false);
        }
    };

    const toggleTransactionModal = () => {
        setTransactionModal(!transactionModal);
        if (!transactionModal) {
            setTransactionData(null);
            setUserDetailsMap({});
        }
    };

    // Handle Winners Modal
    const handleShowWinners = (winners, roundNum) => {
        if (winners && Array.isArray(winners) && winners.length > 0) {
            setWinnersData(winners);
            setRoundNumber(roundNum);

            // Find round details from transactionData if available
            if (transactionData && transactionData.jokerRoundDetails) {
                const roundDetail = transactionData.jokerRoundDetails.find(
                    r => r.roundNumber === roundNum
                );
                setRoundDetails(roundDetail);
            }

            setWinnersModal(true);
        }
    };

    // Handle showing winners from transactions modal (for a specific round)
    const handleShowRoundWinners = (roundNum) => {
        if (transactionData && transactionData.jokerRoundDetails) {
            const roundDetail = transactionData.jokerRoundDetails.find(
                r => r.roundNumber === roundNum
            );
            if (roundDetail && roundDetail.winners && roundDetail.winners.length > 0) {
                setWinnersData(roundDetail.winners);
                setRoundNumber(roundNum);
                setRoundDetails(roundDetail);
                setWinnersModal(true);
            }
        }
    };

    const toggleWinnersModal = () => {
        setWinnersModal(!winnersModal);
        if (!winnersModal) {
            setWinnersData([]);
            setRoundNumber(null);
            setRoundDetails(null);
        }
    };

    // Filter Component to be passed to CommonDataTable
    const filterJsx = (
        <div className="d-flex gap-2 align-items-center my-2 flex-wrap">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Flatpickr
                    key={`datepicker-${activeTab}`} // Force re-render when tab changes
                    className="form-control"
                    placeholder="Start Date to End Date"
                    options={{
                        mode: "range",
                        dateFormat: "Y-m-d"
                    }}
                    value={dateRange}
                    onChange={(selectedDates) => {
                        if (activeTab === "1") {
                            setGeneralDateRange(selectedDates);
                            if (selectedDates.length === 2) {
                                const start = selectedDates[0];
                                const end = selectedDates[1];
                                const formatDate = (d) => {
                                    let month = '' + (d.getMonth() + 1);
                                    let day = '' + d.getDate();
                                    const year = d.getFullYear();
                                    if (month.length < 2) month = '0' + month;
                                    if (day.length < 2) day = '0' + day;
                                    return [year, month, day].join('-');
                                };
                                setGeneralStartDate(formatDate(start));
                                setGeneralEndDate(formatDate(end));
                            } else if (selectedDates.length === 0) {
                                setGeneralStartDate("");
                                setGeneralEndDate("");
                            }
                        } else {
                            setJokerDateRange(selectedDates);
                            if (selectedDates.length === 2) {
                                const start = selectedDates[0];
                                const end = selectedDates[1];
                                const formatDate = (d) => {
                                    let month = '' + (d.getMonth() + 1);
                                    let day = '' + d.getDate();
                                    const year = d.getFullYear();
                                    if (month.length < 2) month = '0' + month;
                                    if (day.length < 2) day = '0' + day;
                                    return [year, month, day].join('-');
                                };
                                setJokerStartDate(formatDate(start));
                                setJokerEndDate(formatDate(end));
                            } else if (selectedDates.length === 0) {
                                setJokerStartDate("");
                                setJokerEndDate("");
                            }
                        }
                    }}
                    style={{ width: "210px" }}
                />

                <select
                    className="form-select"
                    style={{
                        width: "150px"
                    }}
                    value={
                        startDate === todayStr() && endDate === todayStr() ? "Today" :
                            startDate === yesterdayStr() && endDate === yesterdayStr() ? "Yesterday" :
                                startDate === last7DaysStr() && endDate === todayStr() ? "7 Days" :
                                    startDate === thisMonthStartStr() && endDate === todayStr() ? "This Month" : "Custom Range"
                    }
                    onChange={(e) => {
                        const val = e.target.value;
                        let start = startDate, end = endDate;
                        if (val === "Today") { start = todayStr(); end = todayStr(); }
                        else if (val === "Yesterday") { start = yesterdayStr(); end = yesterdayStr(); }
                        else if (val === "7 Days") { start = last7DaysStr(); end = todayStr(); }
                        else if (val === "This Month") { start = thisMonthStartStr(); end = todayStr(); }
                        else { return; }

                        if (activeTab === "1") {
                            setGeneralStartDate(start);
                            setGeneralEndDate(end);
                            setGeneralDateRange([new Date(start), new Date(end)]);
                        } else {
                            setJokerStartDate(start);
                            setJokerEndDate(end);
                            setJokerDateRange([new Date(start), new Date(end)]);
                        }
                    }}
                >
                    <option value="Custom Range">Custom Range</option>
                    <option value="Today">Today</option>
                    <option value="Yesterday">Yesterday</option>
                    <option value="7 Days">Last 7 Days</option>
                    <option value="This Month">This Month</option>
                </select>
                <select
                    key={`operator-${activeTab}`}
                    className="form-select"
                    style={{ width: "200px" }}
                    value={activeTab === "1" ? generalOperatorId : jokerOperatorId}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (activeTab === "1") {
                            setGeneralOperatorId(val);
                            setGeneralUserId("");
                        } else {
                            setJokerOperatorId(val);
                            setJokerUserId("");
                        }
                    }}
                >
                    <option value="">All Operators</option>
                    {operators.map(op => (
                        <option key={op._id} value={op._id}>{op.username || op.email}</option>
                    ))}
                </select>
                <select
                    key={`user-${activeTab}`}
                    className="form-select"
                    style={{
                        width: "200px",
                        display: (activeTab === "1" ? generalOperatorId : jokerOperatorId) ? 'block' : 'none'
                    }}
                    value={activeTab === "1" ? generalUserId : jokerUserId}
                    onChange={(e) => activeTab === "1" ? setGeneralUserId(e.target.value) : setJokerUserId(e.target.value)}
                    disabled={(activeTab === "1" ? !generalOperatorId : !jokerOperatorId) || loadingUsers}
                >
                    <option value="">{loadingUsers ? "Loading Users..." : "All Users"}</option>
                    {(activeTab === "1" ? generalUsers : jokerUsers).map(u => (
                        <option key={u._id} value={u.acnumber}>{u.acnumber || u.username || u.email}</option>
                    ))}
                </select>
                {activeTab === "1" && (
                    <select
                        className="form-select"
                        style={{ width: "160px" }}
                        value={generalGameMode}
                        onChange={(e) => {
                            setGeneralGameMode(e.target.value);
                            generalParamsRef.current.page = 1;
                            setGeneralParams(prev => ({ ...prev, page: 1 }));
                        }}
                    >
                        <option value="all">All Game Modes</option>
                        <option value="easy">Easy Mode</option>
                        <option value="hard">Hard Mode</option>
                        <option value="more_easy">More Easy Mode</option>
                        <option value="medium">Medium Mode</option>
                    </select>
                )}
                <Button color="primary" onClick={handleFilter}>
                    Filter
                </Button>
            </div>
        </div>
    );

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const params = {
                gameType: 'lt',
                startDate: activeTab === "1" ? generalStartDate : jokerStartDate,
                endDate: activeTab === "1" ? generalEndDate : jokerEndDate,
                operatorId: activeTab === "1" ? generalOperatorId : jokerOperatorId,
                userAcNumber: activeTab === "1" ? generalUserId : jokerUserId,
                export: 'excel'
            };
            const response = await exportTransactions(params);

            if (response && response.data) {
                // Handle direct data response format
                const { exportData, columns, filename, summary } = response.data;

                // Debug: Log summary to verify it's being received
                console.log("Export Response - Summary:", summary);

                if (!exportData || !Array.isArray(exportData)) {
                    toast.error("No data available to export");
                    return;
                }

                // Create workbook
                const wb = XLSX.utils.book_new();
                const ws = {};

                let currentRow = 0;

                // Add summary section at the top if summary exists
                if (summary && typeof summary === 'object' && Object.keys(summary).length > 0) {
                    console.log("Adding summary to Excel");

                    // Format summary with readable labels
                    const labelMap = {
                        totalBetsAmount: 'Total Bets Amount',
                        totalPayoutAmount: 'Total Payout Amount',
                        totalRounds: 'Total Rounds',
                        adminProfit: 'Admin Profit',
                        profitMargin: 'Profit Margin (%)',
                        gameName: 'Game Name',
                        reportType: 'Report Type'
                    };

                    // Add summary title
                    ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = { t: 's', v: 'SUMMARY' };
                    currentRow++;

                    // Add summary data
                    Object.keys(summary).forEach(key => {
                        const label = labelMap[key] || key;
                        ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = { t: 's', v: label };
                        ws[XLSX.utils.encode_cell({ r: currentRow, c: 1 })] = { t: summary[key] === null || summary[key] === undefined ? 's' : typeof summary[key] === 'number' ? 'n' : 's', v: summary[key] };
                        currentRow++;
                    });

                    // Add empty row separator
                    currentRow++;
                }

                // Add column headers
                columns.forEach((col, colIndex) => {
                    ws[XLSX.utils.encode_cell({ r: currentRow, c: colIndex })] = { t: 's', v: col.header };
                });
                currentRow++;

                // Add transaction data rows
                exportData.forEach(row => {
                    columns.forEach((col, colIndex) => {
                        const value = row[col.key];
                        const cellValue = value !== undefined && value !== null ? value : '';
                        const cellType = typeof cellValue === 'number' ? 'n' : 's';
                        ws[XLSX.utils.encode_cell({ r: currentRow, c: colIndex })] = { t: cellType, v: cellValue };
                    });
                    currentRow++;
                });

                // Set column widths
                const colWidths = columns.map(col => ({ wch: col.width || 15 }));
                ws['!cols'] = colWidths;

                // Set range
                ws['!ref'] = XLSX.utils.encode_range({
                    s: { r: 0, c: 0 },
                    e: { r: currentRow - 1, c: columns.length - 1 }
                });

                // Add worksheet to workbook
                XLSX.utils.book_append_sheet(wb, ws, "Transactions");

                // Download file
                const fileName = filename || 'lt-transaction.xlsx';
                XLSX.writeFile(wb, fileName);

                toast.success("Export completed successfully");
            } else {
                toast.error("Invalid response from server");
            }
        } catch (err) {
            console.error("Export failed", err);
            toast.error(err.message || "Failed to export transactions");
        } finally {
            setIsExporting(false);
        }
    };

    // Use the summary state that contains totals across all pages
    const generalSummaryMetrics = [
        { label: "Total Bets", value: generalSummary.totalBets, color: "primary" },
        { label: "Total Payout", value: generalSummary.totalPayout, color: "warning" },
        { label: "Admin P&L", value: generalSummary.adminPnL, color: "success" },
        { label: "Total Rounds", value: generalSummary.totalRounds, color: "info" }
    ];

    const jokerSummaryMetrics = [
        { label: "Total Bets", value: jokerData.summary?.totalBetsAmount, color: "primary" },
        { label: "Total Payout", value: jokerData.summary?.totalPayoutAmount, color: "warning" },
        { label: "Total Sessions", value: jokerData.summary?.totalSessions, color: "info" }
    ];

    return (
        <div className="page-content">
            <Container fluid>
                <div className="page-title-box d-flex align-items-center justify-content-between">
                    <h4 className="mb-0">Transactions</h4>
                </div>

                <Row className="mt-5 p-0 mb-4">
                    <Col lg={12} className="p-0">
                        <Card>
                            <CardHeader>
                                <Nav tabs className="nav-tabs-custom card-header-tabs border-bottom-0">
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "1" })}
                                            onClick={() => toggleTab("1")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            P&L
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "2" })}
                                            onClick={() => toggleTab("2")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            Joker
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </CardHeader>

                            <CardBody>
                                <TabContent activeTab={activeTab}>
                                    {/* GENERAL P&L TAB */}
                                    <TabPane tabId="1">
                                        <TransactionTab
                                            summaryMetrics={generalSummaryMetrics}
                                            columns={GeneralPnLColumns(handleShowWinners)}
                                            data={generalData.rounds || generalData.history || []}
                                            loading={generalLoading}
                                            totalRows={generalData.pagination?.total || 0}
                                            params={generalParams}
                                            fetchData={fetchGeneralData}
                                            customFilters={filterJsx}
                                            onExport={handleExport}
                                            isExporting={isExporting}
                                            onRefresh={handleRefresh}
                                            refreshLoading={refreshLoading}
                                        />
                                    </TabPane>

                                    {/* JOKER TAB */}
                                    <TabPane tabId="2">
                                        <TransactionTab
                                            summaryMetrics={jokerSummaryMetrics}
                                            columns={JokerPnLColumns(handleTransactionClick)}
                                            data={jokerData.rounds || jokerData.history || []}
                                            loading={jokerLoading}
                                            totalRows={jokerData.pagination?.total || 0}
                                            params={jokerParams}
                                            fetchData={fetchJokerData}
                                            customFilters={filterJsx}
                                            onExport={handleExport}
                                            isExporting={isExporting}
                                            onRefresh={handleRefresh}
                                            refreshLoading={refreshLoading}
                                        />
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Transaction Modal */}
                <Modal isOpen={transactionModal} toggle={toggleTransactionModal} size="xl">
                    <ModalHeader toggle={toggleTransactionModal}>
                        Joker Session Transactions
                    </ModalHeader>
                    {/* ... existing ModalBody ... */}
                    <ModalBody>
                        {loadingTransactions ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : transactionData ? (
                            <div>
                                {/* ... existing content ... */}
                                {/* Session Info, Stats, Transactions Table */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <p className="mb-1"><strong>Session ID:</strong> {transactionData._id}</p>
                                        <p className="mb-1"><strong>Status:</strong>
                                            <Badge color={
                                                transactionData.status === 'running' ? 'warning' :
                                                    transactionData.status === 'stop' ? 'info' :
                                                        (transactionData.status === 'expire' || transactionData.status === 'expired') ? 'danger' :
                                                            'secondary'
                                            } className="ms-2">
                                                {transactionData.status?.toUpperCase()}
                                            </Badge>
                                        </p>
                                        <p className="mb-1"><strong>Multiplier:</strong> <Badge color="secondary">{transactionData.multiplier}x</Badge></p>
                                    </Col>
                                    <Col md={6}>
                                        <p className="mb-1"><strong>Total Rounds:</strong> {transactionData.totalJokerRounds}</p>
                                        <p className="mb-1"><strong>Triggered Rounds:</strong> {transactionData.triggeredJokerRounds}</p>

                                        <p className="mb-1"><strong>Time:</strong> {
                                            transactionData.startTime && transactionData.endTime ? (() => {
                                                const startDate = new Date(transactionData.startTime);
                                                const endDate = new Date(transactionData.endTime);

                                                const formatTime = (date) => {
                                                    let hours = date.getHours();
                                                    const minutes = date.getMinutes();
                                                    const ampm = hours >= 12 ? 'PM' : 'AM';
                                                    hours = hours % 12;
                                                    hours = hours ? hours : 12; // the hour '0' should be '12'
                                                    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
                                                    return `${hours}:${minutesStr} ${ampm}`;
                                                };

                                                return `${formatTime(startDate)} to ${formatTime(endDate)}`;
                                            })() : 'N/A'
                                        }</p>
                                    </Col>
                                </Row>

                                {/* Stats */}
                                {transactionData.stats && (
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Card className="bg-light">
                                                <CardBody>
                                                    <Row>
                                                        <Col md={4}>
                                                            <p className="mb-0"><strong>Total Bets:</strong> {transactionData.stats.totalBets || 0}</p>
                                                        </Col>
                                                        <Col md={4}>
                                                            <p className="mb-0"><strong>Total Payout:</strong> {transactionData.stats.totalPayout || 0}</p>
                                                        </Col>
                                                        <Col md={4}>
                                                            <p className="mb-0"><strong>Total Winners:</strong> {transactionData.stats.totalWinners || 0}</p>
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                )}

                                {/* Transactions Table */}
                                <h6 className="mb-3">Transactions</h6>
                                {transactionData.transactions && Array.isArray(transactionData.transactions) && transactionData.transactions.length > 0 ? (
                                    <div className="table-responsive">
                                        <Table className="table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Round Number</th>
                                                    <th>User Account Number</th>
                                                    <th>Type</th>
                                                    <th>User Return</th>
                                                    <th>Amount</th>
                                                    <th>Timestamp</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    // Sort transactions by round number (descending) then by timestamp
                                                    const sortedTransactions = [...transactionData.transactions].sort((a, b) => {
                                                        if (b.roundNumber !== a.roundNumber) {
                                                            return b.roundNumber - a.roundNumber;
                                                        }
                                                        return new Date(b.timestamp) - new Date(a.timestamp);
                                                    });

                                                    // Group transactions by round to show "more" link
                                                    const transactionsByRound = {};
                                                    sortedTransactions.forEach(transaction => {
                                                        const roundNum = transaction.roundNumber;
                                                        if (!transactionsByRound[roundNum]) {
                                                            transactionsByRound[roundNum] = [];
                                                        }
                                                        transactionsByRound[roundNum].push(transaction);
                                                    });

                                                    return sortedTransactions.map((transaction, index) => {
                                                        const userInfo = userDetailsMap[transaction.userId] || {};
                                                        const roundNum = transaction.roundNumber;

                                                        // Check if this round has multiple unique users
                                                        const roundTransactions = transactionsByRound[roundNum] || [];
                                                        const uniqueUsers = new Set(roundTransactions.map(t => t.userId).filter(Boolean));
                                                        const hasMultipleUsers = uniqueUsers.size > 1;

                                                        // Check if this is the first transaction of this round
                                                        const isFirstInRound = index === 0 || sortedTransactions[index - 1].roundNumber !== roundNum;

                                                        // Get round details for totalInvestment
                                                        const roundDetail = transactionData.jokerRoundDetails?.find(
                                                            r => r.roundNumber === roundNum
                                                        );
                                                        const totalInvestment = roundDetail?.totalInvestment || 0;

                                                        return (
                                                            <React.Fragment key={transaction._id || index}>
                                                                <tr>
                                                                    <td>#{transaction.roundNumber || '-'}</td>
                                                                    <td>
                                                                        <div>
                                                                            {userInfo.email && userInfo.email !== 'N/A' ? (
                                                                                userInfo.email
                                                                            ) : (
                                                                                transaction.accountNumber || '-'
                                                                            )}
                                                                            {isFirstInRound && hasMultipleUsers && (
                                                                                <div className="mt-1">
                                                                                    <a
                                                                                        href="#"
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            handleShowRoundWinners(roundNum);
                                                                                        }}
                                                                                        className="text-primary text-decoration-none"
                                                                                        style={{ fontSize: '12px' }}
                                                                                    >
                                                                                        +{uniqueUsers.size - 1} more
                                                                                    </a>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <Badge color={transaction.type === 'joker_win' ? 'success' : 'info'}>
                                                                            {transaction.type || '-'}
                                                                        </Badge>
                                                                    </td>
                                                                    <td>
                                                                        {totalInvestment || 0}
                                                                    </td>
                                                                    <td className={`fw-bold ${getAmountColorClass(transaction.amount || 0)}`}>
                                                                        {transaction.amount || 0}
                                                                    </td>
                                                                    <td>
                                                                        {transaction.timestamp ? (() => {
                                                                            const date = new Date(transaction.timestamp);
                                                                            const day = String(date.getDate()).padStart(2, '0');
                                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                                            const year = date.getFullYear();
                                                                            let hours = date.getHours();
                                                                            const minutes = date.getMinutes();
                                                                            const seconds = date.getSeconds();
                                                                            const ampm = hours >= 12 ? 'PM' : 'AM';
                                                                            hours = hours % 12;
                                                                            hours = hours ? hours : 12; // the hour '0' should be '12'
                                                                            const hoursStr = String(hours).padStart(2, '0');
                                                                            const minutesStr = String(minutes).padStart(2, '0');
                                                                            const secondsStr = String(seconds).padStart(2, '0');
                                                                            return `${day}/${month}/${year}, ${hoursStr}:${minutesStr}:${secondsStr} ${ampm}`;
                                                                        })() : '-'}
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No transactions found for this session.</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-muted">No data available</p>
                            </div>
                        )}
                    </ModalBody>
                </Modal>

                <Modal isOpen={winnersModal} toggle={toggleWinnersModal}>
                    <ModalHeader toggle={toggleWinnersModal}>
                        Round Winners {roundNumber ? `(Round #${roundNumber})` : ''}
                    </ModalHeader>
                    <ModalBody>
                        {winnersData && winnersData.length > 0 ? (
                            <div className="table-responsive">
                                <Table className="table-sm table-nowrap align-middle table-borderless">
                                    <thead className="table-light">
                                        <tr>
                                            <th scope="col">#</th>
                                            <th scope="col">Account ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {winnersData.map((winner, key) => (
                                            <tr key={key}>
                                                <td>{key + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-0">
                                                                {winner.acnumber || winner.accountId || winner.userId || winner.email || 'Unknown'}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-muted">No winners found.</p>
                            </div>
                        )}
                    </ModalBody>
                </Modal>
                
                {/* Winners List Modal */}
                <Modal isOpen={winnersModal} toggle={toggleWinnersModal}>
                    <ModalHeader toggle={toggleWinnersModal}>
                        Round Winners {roundNumber ? `(Round #${roundNumber})` : ''}
                    </ModalHeader>
                    <ModalBody>
                        {winnersData && winnersData.length > 0 ? (
                            <div className="table-responsive">
                                <Table className="table-sm table-nowrap align-middle table-borderless">
                                    <thead className="table-light">
                                        <tr>
                                            <th scope="col">#</th>
                                            <th scope="col">Account ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {winnersData.map((winner, key) => (
                                            <tr key={key}>
                                                <td>{key + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-0">
                                                                {winner.acnumber || winner.accountId || winner.userId || winner.email || 'Unknown'}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-muted">No winners found.</p>
                            </div>
                        )}
                    </ModalBody>
                </Modal>
            </Container>
        </div>
    );
};

export default PnL;